import type { StringIndexedObject } from "@kanda-libs/ks-component-ts";
import type { Company, UserType } from "@kanda-libs/ks-frontend-services";
import * as TE from "fp-ts/lib/TaskEither";
import { pipe } from "fp-ts/lib/function";
import type { IO } from "fp-ts/lib/IO";

export const userIsStaff = (user?: UserType): boolean =>
  user?.email?.includes("@kanda.co.uk") || false;

export const isServer = (): boolean => typeof window === "undefined";

export const formatCompanies = (companies: Company[]) =>
  companies.map((company) => ({
    id: company?.cid,
    companyName:
      company?.limited_company_info?.company_name ||
      company?.sole_trader_info?.trading_name,
    companyType: company?.company_type,
    contactName: company?.contact_info?.contact_name || "No contact name",
    contactEmail: company?.contact_info?.contact_email || "No contact email",
    contactPhone: company?.contact_info?.contact_phone || "No contact phone",
    lifecycle: company?.lifecycle,
    link: { id: company?.cid },
    referral: company?.referral_code || "--",
    type: company?.referral_code === "SOLAR1" ? "Solar" : "Standard",
  }));

export interface FormattedDate {
  time: string;
  date: string;
}

export const formatDate = (timestamp?: Date | string | null): FormattedDate => {
  const date = new Date(timestamp as unknown as number);

  const year = date?.getFullYear();
  const month = `0${date?.getMonth() + 1}`.slice(-2);
  const day = `0${date?.getDate()}`.slice(-2);
  const hours = `0${date?.getHours()}`.slice(-2);
  const mins = `0${date?.getMinutes()}`.slice(-2);
  const secs = `0${date?.getSeconds()}`.slice(-2);

  return {
    time: `${hours}:${mins}:${secs}`,
    date: `${day}/${month}/${year}`,
  };
};

export const formatDecimal = (value?: number | string) => {
  if (!value || value === 0 || value === "0") return "0.00";
  if (typeof value === "string") {
    const asInt = Math.round(parseFloat(value));
    return `${`${asInt}`.slice(0, -2)}.${`${asInt}`.slice(-2)}`;
  }
  const asInt = Math.round(value);
  return `${`${asInt}`.slice(0, -2)}.${`${asInt}`.slice(-2)}`;
};

// This needs to be patched because the AirCall chrome extension
// changes the dom, which causes a breaking error when removeChild
// is called on a node that does not exist
export const patchRemoveChild = () => {
  if (typeof Node === "function" && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    (Node.prototype as StringIndexedObject).removeChild = function (
      child: HTMLElement
    ) {
      if (child.parentNode !== this) {
        if (console) {
          console.error(
            "Cannot remove a child from a different parent",
            child,
            this
          );
        }
        return child;
      }
      return originalRemoveChild.apply(
        this,
        arguments as unknown as [child: Node]
      );
    };
  }
};

export const capitaliseWords = (value: string) => {
  const words = value.split(" ");
  const capitalisedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  );
  return capitalisedWords.join(" ");
};

export const formatPriceForDisplay = (
  input: number,
  includeCurrency = true
) => {
  const price = String(input);

  if (price?.includes(".")) {
    const [pounds, pence] = price.split(".");
    const formattedPence = `${pence}00`.slice(0, 2);
    return `${pounds}.${formattedPence}`;
  }
  const currency = includeCurrency ? "£" : "";
  return `${currency}${price}.00`;
};

export const formatDateForDisplay = (timestamp: Date | null): string => {
  const { date, time } = formatDate(timestamp);

  return [date, time].join(" ");
};

export const extractErrorMessage = (
  error: StringIndexedObject | string
): string => {
  if (typeof error === "string") return error;
  if (!error.message) return "Unknown error";
  const mapping = error.message
    .split(", ")
    .reduce((errorObj: StringIndexedObject, part: string) => {
      const parts = part.split("=");
      if (parts.length !== 2) return errorObj;
      return {
        ...errorObj,
        [parts[0]]: parts[1],
      };
    }, {});
  if (Object.keys(mapping).length === 0) return "Unknown error";
  if (!mapping.message) return "Unknown error";
  return mapping.message;
};

export const handleIO = (io: IO<void>): TE.TaskEither<Error, void> =>
  pipe(
    () => io(),
    TE.fromIO,
    TE.mapLeft((error) => new Error(error))
  );

export const pluralise = (
  count: number,
  singular: string,
  plural: string
): string => (count === 1 ? singular : plural);
